import type { LucideProps } from 'lucide-react';
import {
  LayoutDashboard,
  FileStack,
  UserPlus,
  Users2,
  Users,
  BarChart3,
} from 'lucide-react';

// Local alias for a Lucide icon component reference (mirrors mockNavigation.ts).
type LucideIcon = React.ForwardRefExoticComponent<
  Omit<LucideProps, 'ref'> & React.RefAttributes<SVGSVGElement>
>;

/**
 * Admin console navigation — the single, typed source of truth for the
 * AdminSidebar. Phase 3 lifted these out of the sidebar's inline `enterpriseGroups`
 * so the nav is data-driven and testable (see review/ENTERPRISE_PORTAL_SEPARATION_*).
 *
 * The sidebar still owns presentation and the two conditional cases:
 *   - {@link pendingMembersLink} renders with a live count badge, and
 *   - {@link courseStudioLink} is shown standalone only to non-enterprise admins
 *     (enterprise admins get the same link inside the Learning group below).
 */
export interface AdminNavLink {
  label: string;
  to: string;
  testid: string;
  icon: LucideIcon;
}

export interface AdminNavGroup {
  key: string;
  label: string;
  links: AdminNavLink[];
}

/** Top-level links shown to every admin, above the enterprise console groups. */
export const adminTopLinks: AdminNavLink[] = [
  // Cross-outline authoring overview (folded under /admin/tutor/* in Phase 3).
  { label: 'Admin dashboard', to: '/admin/tutor/dashboard', testid: 'sidebar-admin-dashboard-link', icon: LayoutDashboard },
];

/**
 * Standalone Course Studio link — rendered only for non-enterprise admins.
 * Enterprise admins reach Course Studio through the Learning group (same testid,
 * mutually exclusive), so the id stays stable across both placements.
 */
export const courseStudioLink: AdminNavLink = {
  label: 'Course Studio',
  to: '/admin/course-studio',
  testid: 'sidebar-course-studio-link',
  icon: FileStack,
};

/** Pending join-requests — rendered with a live count badge in the sidebar. */
export const pendingMembersLink: AdminNavLink = {
  label: 'Pending members',
  to: '/admin/pending-members',
  testid: 'sidebar-pending-members-link',
  icon: UserPlus,
};

/**
 * Enterprise console — four labeled groups shown only to admins on the
 * enterprise plan. Hidden-but-routed surfaces (Programs, Content Library,
 * Assessments, Skills) are intentionally omitted here; re-add an entry to
 * restore its link without touching route definitions.
 */
export const enterpriseNavGroups: AdminNavGroup[] = [
  {
    key: 'overview',
    label: 'Overview',
    links: [
      { label: 'Dashboard', to: '/admin/enterprise/dashboard', testid: 'sidebar-ent-dashboard-link', icon: LayoutDashboard },
    ],
  },
  {
    key: 'people',
    label: 'People',
    links: [
      { label: 'Teams', to: '/admin/enterprise/team-management', testid: 'sidebar-team-management-link', icon: Users2 },
      // Consolidated People page — employee roster + access/roles under one route.
      { label: 'People', to: '/admin/enterprise/people', testid: 'sidebar-ent-people-link', icon: Users },
    ],
  },
  {
    key: 'learning',
    label: 'Learning',
    links: [
      { ...courseStudioLink },
    ],
  },
  {
    key: 'insights',
    label: 'Insights',
    links: [
      { label: 'Reports', to: '/admin/enterprise/reports', testid: 'sidebar-ent-reports-link', icon: BarChart3 },
    ],
  },
];
